import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {Alert, AlertTitle, Box, Card, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import Form, {MuiRFF, Yup} from "../../form";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Grid2 from "@mui/material/Unstable_Grid2";
import VendorAutocomplete from "../../text-fields/autocomplete/vendor-autocomplete";
import PartAutocomplete from "../../text-fields/autocomplete/part-autocomplete";
import DateUtils from "../../../../core/services/date-utils";
import AddVendorModal from "../add-vendor";
import useAccessLevels from "../../../hooks/use-access-levels";
import ToastService from "../../../../core/services/toast";
import {formatMoney} from "../../../../core/services/utils/utils";
import ApiResponseCodes from "../../../../core/models/static/api-response-codes";

const initialAddVendorDialogState = {open: false, setOpen: () => void 0, initialValues: {}, addVendor: () => void 0};

/**
 * Upsert vendor–part association.
 * - Product context: pass `partId` — pick vendor.
 * - Vendor context: pass `vendorId` — pick part via PartAutocomplete.
 */
const UpsertVendorPartDialog = ({
                                    open,
                                    record: recordProps,
                                    partId: partIdProp,
                                    partNo: partNoProp,
                                    vendorId: vendorIdProp,
                                    vendorName: vendorNameProp,
                                    vendorCode: vendorCodeProp,
                                    close,
                                    onSuccess,
                                }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.upsert-vendor-part'});
    const [submitting, setSubmitting] = useState(false);
    const [record, setRecord] = useState(recordProps);
    const [conflictingRecord, setConflictingRecord] = useState(null);
    const [removingIds, setRemovingIds] = useState([]);
    const removedIdsRef = useRef([]);
    const [addVendorDialogState, setAddVendorDialogState] = useState(initialAddVendorDialogState);
    const formRef = useRef(null);
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();

    const disableDialogClose = useMemo(() => submitting || removingIds.length > 0, [removingIds, submitting]);
    const isUpdating = useMemo(() => !!record?.id, [record?.id]);
    /** Vendor-scoped page locks the vendor and requires part selection. */
    const vendorLocked = useMemo(() => Number.isFinite(Number(vendorIdProp)), [vendorIdProp]);
    /** Product-scoped tab locks the part and requires vendor selection. */
    const partLocked = useMemo(() => Number.isFinite(Number(partIdProp)) || !!partIdProp, [partIdProp]);

    const fixedContextAlert = useMemo(() => {
        if (vendorLocked) {
            const name = vendorNameProp
                ?? record?.vendorName
                ?? null;
            const code = vendorCodeProp
                ?? record?.vendorCode
                ?? null;
            if (name && code) {
                return {
                    title: t('fixedVendorTitle'),
                    message: t('fixedVendorNamed', {name, code}),
                };
            }
            if (name) {
                return {
                    title: t('fixedVendorTitle'),
                    message: t('fixedVendorNameOnly', {name}),
                };
            }
            if (code) {
                return {
                    title: t('fixedVendorTitle'),
                    message: t('fixedVendorCodeOnly', {code}),
                };
            }
            return {
                title: t('fixedVendorTitle'),
                message: t('fixedVendorFallback'),
            };
        }
        if (partLocked) {
            const partNo = partNoProp
                ?? record?.partNo
                ?? record?.partNumber
                ?? "";
            if (partNo) {
                return {
                    title: t('fixedPartTitle'),
                    message: t('fixedPart', {partNo}),
                };
            }
            return {
                title: t('fixedPartTitle'),
                message: t('fixedPartFallback'),
            };
        }
        return null;
    }, [partLocked, partNoProp, record?.partNo, record?.partNumber, record?.vendorCode, record?.vendorName, t, vendorCodeProp, vendorLocked, vendorNameProp]);

    /**
     * Syncs the editable record from props when the dialog opens (layout phase so Form mounts cleanly).
     */
    useLayoutEffect(() => {
        if (open) {
            setRecord(recordProps ?? null);
            setConflictingRecord(null);
            setRemovingIds([]);
            removedIdsRef.current = [];
            setSubmitting(false);
            setAddVendorDialogState(initialAddVendorDialogState);
        } else {
            setRecord(null);
            setConflictingRecord(null);
            formRef.current = null;
        }
    }, [open, recordProps]);

    /**
     * Removes a vendor part by its record id.
     */
    const removeConflicting = useCallback(async (recordId) => {
        setRemovingIds(prevState => [...prevState, recordId]);
        const response = await Api.deleteVendorPart(recordId);
        if (!isMounted()) return false;
        setRemovingIds(prevState => prevState.filter(id => id !== recordId));
        if (response?.resultFlag) {
            removedIdsRef.current = [...removedIdsRef.current, recordId];
        }
        return response?.resultFlag;
    }, [isMounted]);

    /**
     * Removes the conflicting record and resubmits the form.
     */
    const removeConflictingAndResubmit = useCallback(async (recordId) => {
        setSubmitting(true);
        const removeSuccessful = await removeConflicting(recordId);
        if (!isMounted()) return;
        setSubmitting(false);
        if (removeSuccessful) {
            setConflictingRecord(null);
            requestAnimationFrame(() => {
                formRef.current?.submit();
            });
        }
    }, [isMounted, removeConflicting]);

    /**
     * Closes the conflicting record's range and continues with a new start date.
     */
    const closeConflictingAndResubmitWithNewStartDate = useCallback(async (conflict) => {
        setSubmitting(true);
        const closeResponse = await Api.updateVendorPart({
            ...conflict,
            endDateTime: DateUtils.localToServer(DateUtils.localNow()).format(DateUtils.withoutOffsetFormat),
        });
        if (!isMounted()) return;
        setSubmitting(false);
        if (!closeResponse?.resultFlag) return;
        onSuccess(closeResponse?.data);
        setConflictingRecord(null);
        const newStartDateTime = DateUtils.serverToLocal(closeResponse.data.endDateTime).add(1, 'second');
        formRef.current?.change('startDateTime', newStartDateTime);
    }, [isMounted, onSuccess]);

    /**
     * Swaps the editable record with a conflicting one.
     */
    const swapRecordWithConflicting = useCallback((conflicting) => {
        setConflictingRecord(null);
        setRecord(conflicting);
    }, []);

    /**
     * Resolves vendor online profile when vendor is not locked (product context).
     */
    const getOrCreateVendor = useCallback(async (vendorInfo) => {
        const response = await Api.getVendorInformation(vendorInfo.code);
        if (!isMounted())
            return;
        if (!response?.resultFlag)
            return;
        if (response?.data)
            return response?.data;
        if (!operatorAccessLevels.packages.base.createNewVendor) {
            ToastService.warning({
                title: "No Permission",
                message: "The selected vendor needs to have an online profile in our system yet you do not have permission to create them. Please contact your administrator."
            });
            return;
        }
        return await new Promise((resolve) => {
            ToastService.warning({
                title: "Create Vendor Profile",
                message: "The selected vendor needs to have an online profile in our system."
            });
            setAddVendorDialogState({
                open: true,
                initialValues: vendorInfo,
                setOpen: () => {
                    resolve(null);
                    setAddVendorDialogState(initialAddVendorDialogState);
                },
                addVendor: async (forApi, onResponse) => {
                    const createResponse = await Api.createVendorOnlineProfile(forApi);
                    if (createResponse?.resultFlag) {
                        resolve(createResponse?.data);
                    }
                    onResponse(createResponse);
                },
            });
        });
    }, [isMounted, operatorAccessLevels.packages.base.createNewVendor]);

    /**
     * Fetches the upserted record and closes the dialog.
     */
    const fetchRecordAndSubmit = useCallback(async (saved) => {
        const response = await Api.listVendorParts({
            pagination: {currentPage: 1, pageSize: 100},
            vendorIds: [saved.vendorId],
            partIds: [saved.partId],
        });
        if (!isMounted()) return;
        const found = response?.data?.items?.find(r => r.id === saved.id) ?? saved;
        if (response?.resultFlag) {
            onSuccess(found, removedIdsRef.current);
            close();
        }
    }, [close, isMounted, onSuccess]);

    /**
     * Resolves partId / vendorId for create/update payloads from props or form.
     */
    const resolveIds = useCallback(async (values) => {
        /**
         * Coerces an identifier to a number when numeric, otherwise keeps the original.
         */
        const toApiId = (value) => {
            if (value == null || value === '') {
                return undefined;
            }
            const asNumber = Number(value);
            return Number.isFinite(asNumber) ? asNumber : value;
        };

        let resolvedPartId = toApiId(partIdProp ?? record?.partId ?? values.part?.id);

        let resolvedVendorId;
        if (vendorLocked) {
            resolvedVendorId = toApiId(vendorIdProp);
        } else {
            const vendorInfo = await getOrCreateVendor(values.vendor);
            if (!vendorInfo) {
                return null;
            }
            resolvedVendorId = toApiId(vendorInfo.id);
        }

        if (resolvedPartId == null || resolvedVendorId == null) {
            return null;
        }
        return {partId: resolvedPartId, vendorId: resolvedVendorId};
    }, [getOrCreateVendor, partIdProp, record?.partId, vendorIdProp, vendorLocked]);

    /**
     * Creates a new vendor part record.
     */
    const createRecord = useCallback(async (values) => {
        setSubmitting(true);
        const ids = await resolveIds(values);
        if (!isMounted()) return;
        if (!ids) {
            setSubmitting(false);
            return;
        }
        const forApi = {
            partId: ids.partId,
            vendorId: ids.vendorId,
            vendorPartNumber: values.vendorPartNumber?.trim(),
            vendorPrice: values.vendorPrice,
            startDateTime: DateUtils.UTCToServer(values.startDateTime).format(DateUtils.withoutOffsetFormat),
            endDateTime: values.endDateTime
                ? DateUtils.UTCToServer(values.endDateTime).format(DateUtils.withoutOffsetFormat)
                : null,
        };
        const response = await Api.createVendorPart(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            await fetchRecordAndSubmit(response?.data);
        } else if (response?.errorCode === ApiResponseCodes.vendorPartConflictingRecord) {
            setConflictingRecord(response?.data);
        }
        if (!isMounted()) return;
        setSubmitting(false);
    }, [fetchRecordAndSubmit, isMounted, resolveIds]);

    /**
     * Updates an existing vendor part record.
     */
    const updateRecord = useCallback(async (values) => {
        setSubmitting(true);
        const ids = await resolveIds(values);
        if (!isMounted()) return;
        if (!ids) {
            setSubmitting(false);
            return;
        }
        const forApi = {
            id: record?.id,
            partId: ids.partId,
            vendorId: ids.vendorId,
            vendorPartNumber: values.vendorPartNumber?.trim(),
            vendorPrice: values.vendorPrice,
            startDateTime: DateUtils.UTCToServer(values.startDateTime).format(DateUtils.withoutOffsetFormat),
            endDateTime: values.endDateTime
                ? DateUtils.UTCToServer(values.endDateTime).format(DateUtils.withoutOffsetFormat)
                : null,
        };
        const response = await Api.updateVendorPart(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            await fetchRecordAndSubmit(response?.data);
        } else if (response?.errorCode === ApiResponseCodes.vendorPartConflictingRecord) {
            setConflictingRecord(response?.data);
        }
        if (!isMounted()) return;
        setSubmitting(false);
    }, [fetchRecordAndSubmit, isMounted, record?.id, resolveIds]);

    /**
     * Submits create or update.
     */
    const submitForm = useCallback(async (values) => {
        if (isUpdating) {
            await updateRecord(values);
        } else {
            await createRecord(values);
        }
    }, [createRecord, isUpdating, updateRecord]);

    const schema = useMemo(() => Yup.object().shape({
        vendor: vendorLocked
            ? Yup.mixed().nullable()
            : Yup.mixed().nullable().required(ValidateMessages.required),
        part: partLocked
            ? Yup.mixed().nullable()
            : Yup.mixed().nullable().required(ValidateMessages.required),
        vendorPartNumber: Yup.string().trim().nullable().required(ValidateMessages.required),
        vendorPrice: Yup.number().nullable().required(ValidateMessages.required)
            .typeError(ValidateMessages.incorrectType('number'))
            .min(0, ValidateMessages.greaterThan(formatMoney(0))),
        startDateTime: Yup.mixed()
            .nullable()
            .required(ValidateMessages.required)
            .validDate(ValidateMessages.incorrect)
            .maxDate(Yup.ref("endDateTime"), ValidateMessages.lessThan("End Date")),
        endDateTime: Yup.mixed()
            .nullable()
            .validDate(ValidateMessages.incorrect)
            .minDate(Yup.ref("startDateTime"), ValidateMessages.greaterThan("Start Date")),
    }), [partLocked, vendorLocked]);

    const initialValues = useMemo(() => {
        const partIdFromRecord = record?.partId ?? record?.part?.id;
        const partNoFromRecord = record?.partNo ?? record?.partNumber ?? record?.part?.partNo;
        return ({
            vendor: {
                code: record?.vendorCode ?? '',
                id: record?.vendorId,
            },
            part: partIdFromRecord
                ? {
                    id: partIdFromRecord,
                    partNo: partNoFromRecord ?? String(partIdFromRecord),
                }
                : null,
            vendorPartNumber: record?.vendorPartNumber ?? '',
            vendorPrice: record?.vendorPrice ?? '',
            startDateTime: record?.startDateTime ? DateUtils.serverToUTC(record.startDateTime) : undefined,
            endDateTime: record?.endDateTime ? DateUtils.serverToUTC(record.endDateTime) : undefined,
        });
    }, [record]);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const dialogTitle = useMemo(() => {
        if (conflictingRecord) {
            return "Conflicting Record Found";
        }
        if (vendorLocked) {
            return isUpdating ? "Edit Vendor Product" : "Add Vendor Product";
        }
        return isUpdating ? "Edit Product's Vendor" : "Create Product's Vendor";
    }, [conflictingRecord, isUpdating, vendorLocked]);

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => !disableDialogClose ? close() : void 0}
                maxWidth={'sm'}
                disableBackdropClick={disableDialogClose}
                disableEscapeKeyDown={disableDialogClose}
            >
                <DialogTitle>
                    {dialogTitle}
                </DialogTitle>
                <DialogContent dividers>
                    {
                        open &&
                        <Form
                            key={record?.id ?? 'create'}
                            onSubmit={submitForm}
                            validate={validate}
                            initialValues={initialValues}
                            formProps={{id: 'upsert-vendor-part-dialog'}}
                        >
                            {({form, values}) => {
                                formRef.current = form;
                                return (
                                    <Grid2 spacing={2} container>
                                        {
                                            !!fixedContextAlert &&
                                            <Grid2 xs={12}>
                                                <Alert
                                                    severity={'info'}
                                                    variant={'outlined'}
                                                    sx={{borderColor: 'info.main', width: '100%'}}
                                                >
                                                    <AlertTitle sx={{mb: 0.5}}>
                                                        {fixedContextAlert.title}
                                                    </AlertTitle>
                                                    {fixedContextAlert.message}
                                                </Alert>
                                            </Grid2>
                                        }
                                        {
                                            !!conflictingRecord &&
                                            <Grid2 xs={12}>
                                                <Card variant={'outlined'} sx={{padding: 2}}>
                                                    <Grid2 container spacing={1}>
                                                        <Grid2 xs={12}>
                                                            <Typography variant={'subtitle1'}>
                                                                Conflicting Record:
                                                            </Typography>
                                                        </Grid2>
                                                        {
                                                            vendorLocked
                                                                ? (
                                                                    <Grid2 xs={12}>
                                                                        <Typography variant={'body2'}>
                                                                            Part No: <Typography component={'span'} variant={'body2'} color={'tertiary.main'}>
                                                                            {conflictingRecord.partNo
                                                                                ?? conflictingRecord.partNumber
                                                                                ?? conflictingRecord.partId
                                                                                ?? '--'}
                                                                        </Typography>
                                                                        </Typography>
                                                                    </Grid2>
                                                                )
                                                                : (
                                                                    <Grid2 xs={12}>
                                                                        <Typography variant={'body2'}>
                                                                            Vendor Code: <Typography component={'span'} variant={'body2'} color={'tertiary.main'}>
                                                                            {conflictingRecord.vendorCode}
                                                                        </Typography>
                                                                        </Typography>
                                                                    </Grid2>
                                                                )
                                                        }
                                                        <Grid2 xs={12} md={6}>
                                                            <Typography variant={'body2'}>
                                                                Vendor Part Number: <Typography component={'span'} variant={'body2'} color={'tertiary.main'}>
                                                                {conflictingRecord.vendorPartNumber}
                                                            </Typography>
                                                            </Typography>
                                                        </Grid2>
                                                        <Grid2 xs={12} md={6}>
                                                            <Typography variant={'body2'}>
                                                                Vendor Price: <Typography component={'span'} variant={'body2'} color={'tertiary.main'}>
                                                                {conflictingRecord.vendorPrice}
                                                            </Typography>
                                                            </Typography>
                                                        </Grid2>
                                                        <Grid2 xs={12} md={6}>
                                                            <Typography variant={'body2'}>
                                                                Start Date: <Typography component={'span'} variant={'body2'} color={'tertiary.main'}>
                                                                {DateUtils.serverToLocal(conflictingRecord.startDateTime)?.format(DateUtils.dateTimeFormat)}
                                                            </Typography>
                                                            </Typography>
                                                        </Grid2>
                                                        <Grid2 xs={12} md={6}>
                                                            <Typography variant={'body2'}>
                                                                End Date: <Typography component={'span'} variant={'body2'} color={'tertiary.main'}>
                                                                {conflictingRecord.endDateTime
                                                                    ? DateUtils.serverToLocal(conflictingRecord.endDateTime)?.format(DateUtils.dateTimeFormat)
                                                                    : "Never"
                                                                }
                                                            </Typography>
                                                            </Typography>
                                                        </Grid2>
                                                        <Grid2 xs={12}>
                                                            <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap', marginBlockStart: 1}}>
                                                                <Button
                                                                    variant={'outlined'}
                                                                    color={'error'}
                                                                    onClick={() => removeConflictingAndResubmit(conflictingRecord.id)}
                                                                    disabled={removingIds.includes(conflictingRecord.id)}
                                                                >
                                                                    Remove & Submit
                                                                </Button>
                                                                <Button
                                                                    variant={'outlined'}
                                                                    color={'secondary'}
                                                                    onClick={() => swapRecordWithConflicting(conflictingRecord)}
                                                                    disabled={removingIds.includes(conflictingRecord.id)}
                                                                >
                                                                    Edit This
                                                                </Button>
                                                                <Button
                                                                    variant={'outlined'}
                                                                    color={'primary'}
                                                                    onClick={() => closeConflictingAndResubmitWithNewStartDate(conflictingRecord)}
                                                                    disabled={removingIds.includes(conflictingRecord.id)}
                                                                >
                                                                    Close This & Continue
                                                                </Button>
                                                            </Box>
                                                        </Grid2>
                                                    </Grid2>
                                                </Card>
                                            </Grid2>
                                        }
                                        {
                                            !vendorLocked &&
                                            <Grid2 xs={12}>
                                                <VendorAutocomplete
                                                    form
                                                    name={'vendor'}
                                                    label={'Vendor'}
                                                    required={required.vendor}
                                                    disabled={submitting}
                                                    fullWidth
                                                />
                                            </Grid2>
                                        }
                                        {
                                            !partLocked &&
                                            <Grid2 xs={12}>
                                                <PartAutocomplete
                                                    form
                                                    name={'part'}
                                                    label={'Part Number'}
                                                    required={required.part}
                                                    disabled={submitting || isUpdating}
                                                    fullWidth
                                                />
                                            </Grid2>
                                        }
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'vendorPartNumber'}
                                                label={'Vendor Part Number'}
                                                required={required.vendorPartNumber}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.TextField
                                                name={'vendorPrice'}
                                                label={'Vendor Price'}
                                                required={required.vendorPrice}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.DateTimePicker
                                                name={'startDateTime'}
                                                label={'Start Date'}
                                                required={required.startDateTime}
                                                maxDateTime={values.endDateTime}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={6}>
                                            <MuiRFF.DateTimePicker
                                                name={'endDateTime'}
                                                label={'End Date'}
                                                required={required.endDateTime}
                                                minDateTime={values.startDateTime}
                                                disabled={submitting}
                                                fullWidth
                                            />
                                        </Grid2>
                                    </Grid2>
                                );
                            }}
                        </Form>
                    }
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                        <Button
                            disabled={disableDialogClose}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => close()}
                            borderRadius={disableDialogClose ? '8px 0 8px 0' : '0 8px 0 8px'}
                        >
                            Close
                        </Button>
                        {
                            !conflictingRecord &&
                            <Button
                                disabled={submitting}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={'upsert-vendor-part-dialog'}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {submitting ? "Submitting..." : isUpdating ? "Update" : "Create"}
                            </Button>
                        }
                    </Box>
                </DialogActions>
            </Dialog>
            <AddVendorModal
                addVendor={addVendorDialogState.addVendor}
                initialValues={addVendorDialogState.initialValues}
                open={addVendorDialogState.open}
                setOpen={addVendorDialogState.setOpen}
            />
        </>
    );
};

export default UpsertVendorPartDialog;
